import { FormSectionObject, GenericFormField, GenericFormSection } from "models/propTypes/genericFormProps";
import useCheckRequired from "./useCheckRequired";
import useGetHasValueCountWithContext from "./useGetHasValueCountWithContext";
import useCheckHasErrors from "./useCheckHasErrors";
import useGetTotalFields from "./useGetTotalFields";
import useCheckVisibility from "./useCheckVisibility";
import { FormEnums } from "models/enums";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";

function useFormSectionObject<T extends Record<string, any>>(
  validationSchema?: ObjectSchema<any>,
  checkInitialErrors?: boolean
) {
  const { getFieldRequired } = useCheckRequired(validationSchema);
  const { getHasValueCount } = useGetHasValueCountWithContext<T>();
  const { getHasErrors } = useCheckHasErrors<T>(checkInitialErrors);
  const { getTotal } = useGetTotalFields<T>();
  const { getFieldHidden } = useCheckVisibility<T>();

  const getFormSectionValues = (
    field: GenericFormField<T> | GenericFormSection<T>,
    name?: string
  ): FormSectionObject => {
    const isHidden = field.hidden ? getFieldHidden(field.hidden) : false;
    if (!isHidden) {
      if (field.type !== FormEnums.FormFieldType.EDITABLETABLE && field.innerFields !== undefined) {
        const innerObj = Array.isArray(field.innerFields)
          ? (field.innerFields as any).map((innerField: GenericFormSection<T>) => getFormSectionValues(innerField))
          : Object.entries(field.innerFields).map(
              ([key, innerField]) => innerField && getFormSectionValues(innerField, key)
            );

        const hasValueCount: number = innerObj
          .map((sectionObj: FormSectionObject) => sectionObj.hasValueCount)
          .reduce((tot: number, val: number) => tot + val, 0);

        const total: number = innerObj
          .map((sectionObj: FormSectionObject) => sectionObj.total)
          .reduce((tot: number, val: number) => tot + val, 0);

        const formField = {
          hasValueCount: hasValueCount,
          hasRequired: innerObj.some((val: FormSectionObject) => val.hasRequired),
          hasErrors: innerObj.some((val: FormSectionObject) => val.hasErrors),
          total: total,
        };

        return formField;
      } else {
        field = field as GenericFormField<T>;

        const hasValueCount = getHasValueCount(field, name);
        const required = name ? getFieldRequired(name) : false;
        const hasErrors = getHasErrors(field, name);
        const total = getTotal(field, name);

        const formField = {
          hasValueCount: hasValueCount,
          hasRequired: required,
          hasErrors: hasErrors,
          total: total,
        };

        return formField;
      }
    } else
      return {
        hasValueCount: 0,
        hasRequired: false,
        hasErrors: false,
        total: 0,
      };
  };

  return { getFormSectionValues };
}

export default useFormSectionObject;
